import { useEffect, useState } from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../App";
import ScrollTopButton from "./ScrollTop";
import SidebarProfile from "./SidebarProfile";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faBarcode,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import swal from "sweetalert";
import ReactPaginate from "react-paginate";
import "../styles/profile.css";
import BrianAnimated from "./BrainAnimated";
import StonesLoader from "./Stones";
import BinocularsLoader from "./BinocularsLoader";

// ===== دوال الحماية من XSS لصفحة شحن المحفظة =====

// تنظيف المدخلات
const sanitizeWalletInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/[<>'"&]/g, (match) => {
      const entityMap = {
        '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#x27;', '&': '&amp;'
      };
      return entityMap[match];
    });
};

// فحص أكواد XSS
const detectWalletXSS = (input) => {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    /alert\s*\(/i, /confirm\s*\(/i, /prompt\s*\(/i, /document\./i,
    /window\./i, /eval\s*\(/i, /<script/i, /<iframe/i, /javascript:/i,
    /vbscript:/i, /on\w+\s*=/i, /union\s+select/i, /drop\s+table/i
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

// التحقق من صحة كود الشحن
const validateChargeCode = (code) => {
  if (typeof code !== 'string') return false;
  
  // كود الشحن: أرقام وحروف فقط، بدون رموز خاصة
  const validCodePattern = /^[a-zA-Z0-9\-_]{1,50}$/;
  return validCodePattern.test(code) && code.length <= 50;
};

// التحقق من صحة مبلغ فوري
const validateFawryAmount = (amount) => {
  if (typeof amount !== 'string') return false;
  
  // مبلغ فوري: أرقام فقط
  const validAmountPattern = /^\d{1,10}$/;
  const numAmount = parseInt(amount);
  return validAmountPattern.test(amount) && numAmount > 0 && numAmount <= 10000;
};

// دالة التنظيف الشاملة
const secureWalletInput = (input, fieldType) => {
  if (!input || typeof input !== 'string') return input;
  
  if (detectWalletXSS(input)) {
    console.warn(`XSS attempt detected in wallet ${fieldType}: ${input}`);
    return '';
  }
  
  // تطبيق validation حسب نوع الحقل
  if (fieldType === 'code' && !validateChargeCode(input)) {
    console.warn(`Invalid charge code format: ${input}`);
    return '';
  }
  
  if (fieldType === 'amount' && !validateFawryAmount(input)) {
    console.warn(`Invalid fawry amount: ${input}`);
    return '';
  }
  
  return sanitizeWalletInput(input.trim());
};

function ProfileWallet({ style, auth, setAuth }) {
  const [codesTable, setCodesTable] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [code, setCode] = useState("");
  const [fawryAmount, setFawryAmount] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPagingLoading, setIsPagingLoading] = useState(false);
  const [key, setKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();
  const itemsPerPage = 10;

useEffect(() => {
  window.scrollTo(0, 0);

  const fetchData = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");

    const delay = new Promise(resolve => setTimeout(resolve, 2000)); // ⏱️ تأخير 3 ثوانٍ
    const request = axios.get(`${BASE_URL}/api/my-codes-charge-page`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    try {
      const [response] = await Promise.all([request, delay]);

      setCodesTable(Object.values(response.data.data || {}));
      setWalletBalance(auth.balance || 0);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  fetchData();
}, [auth.balance, key]);


  const handleCharge = async (event) => {
    event.preventDefault();
    
    // فحص أمني شامل قبل الإرسال
    if (!code) {
      swal("خطأ!", "يرجى إدخال كود الشحن.", "warning");
      return;
    }
    
    if (detectWalletXSS(code)) {
      swal("خطأ أمني!", "كود الشحن يحتوي على محتوى غير آمن", "error");
      return;
    }
    
    if (!validateChargeCode(code)) {
      swal("خطأ!", "كود الشحن غير صحيح. يجب أن يحتوي على أرقام وحروف فقط", "warning");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      
      // تنظيف الكود مرة أخيرة قبل الإرسال
      const sanitizedCode = secureWalletInput(code, 'code');
      
      const response = await axios.post(
        `${BASE_URL}/api/codes/charge`,
        { code: sanitizedCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // تحديث الرصيد مباشرة من الـ response
      if (response.data?.data?.value) {
        const newBalance = (auth.balance || 0) + response.data.data.value;
        setWalletBalance(newBalance);
        
        // تحديث الـ auth state الرئيسي
        if (setAuth) {
          setAuth((prevAuth) => ({
            ...prevAuth,
            balance: newBalance
          }));
        }
      }
      
      setCode("");
      swal("تم الشحن بنجاح!", { icon: "success" });
      setKey((prevKey) => prevKey + 1);
    } catch (e) {
      // عرض رسالة الخطأ من الـ response مباشرة
      const errorMessage = e.response?.data?.message || "حدث خطأ ما، يرجى المحاولة مرة أخرى.";
      swal("خطأ!", errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const chargeBalanceWithfawry = async (event) => {
    event.preventDefault();
    
    // فحص أمني شامل لمبلغ فوري
    if (!fawryAmount || fawryAmount <= 0) {
      swal("خطأ!", "يرجى إدخال مبلغ صحيح للشحن.", "warning");
      return;
    }
    
    if (detectWalletXSS(fawryAmount)) {
      swal("خطأ أمني!", "المبلغ يحتوي على محتوى غير آمن", "error");
      return;
    }
    
    if (!validateFawryAmount(fawryAmount)) {
      swal("خطأ!", "المبلغ غير صحيح. يجب أن يكون رقم صحيح بين 1 و 10000", "warning");
      return;
    }
    
    // تنظيف المبلغ قبل الاستخدام
    const sanitizedAmount = secureWalletInput(fawryAmount, 'amount');
    swal({
              title: `هل أنت متأكد من شحن ${sanitizedAmount} جنيه؟`,
      text: "ملحوظة: قد يستغرق وصول الرصيد من نصف ساعة إلى ساعة.",
      icon: "warning",
      buttons: ["إلغاء", "تأكيد"],
      dangerMode: true,
    }).then(async (ok) => {
      if (ok) {
        setIsSubmitting(true);
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `${BASE_URL}/api/fawaterak-pay-initiation/wallet/${sanitizedAmount}/payment-method/3`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const fawryCode = response?.data?.data?.data?.payment_data?.fawryCode;
          if (fawryCode) {
            navigate("/payment", { state: { paymentData: fawryCode } });
          }
        } catch (error) {
          swal(
            "خطأ!",
            "لم نتمكن من إنشاء كود فوري. يرجى المحاولة مرة أخرى.",
            "error"
          );
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };

  function formatDate(dateString) {
    if (!dateString) return "غير محدد";
    try {
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC",
        numberingSystem: "arab",
      };
      return new Intl.DateTimeFormat("ar-EG", options).format(date);
    } catch (e) {
      return "تاريخ غير صالح";
    }
  }

  const offset = currentPage * itemsPerPage;
  const currentItems = codesTable.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(codesTable.length / itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setIsPagingLoading(true);
    setTimeout(() => {
      setCurrentPage(selected);
      setIsPagingLoading(false);
    }, 300);
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => offset + index + 1,
      sortable: true,
      width: "60px",
    },
    { name: "الكود", selector: (row) => row.code, sortable: true, grow: 2 },
    {
      name: "القيمة",
      selector: (row) => `${row.value} جنيه`,
      sortable: true,
      center: true,
    },
    // { name: 'تاريخ الشحن', selector: row => formatDate(row.created_at), sortable: true, center: true },
  ];

  const customStyles = {
    headRow: { style: { fontSize: "0.95rem", fontWeight: "700" } },
    rows: {
      style: { minHeight: "72px", fontSize: "1.1rem", fontWeight: "500" },
    },
  };

  if (isLoading) {
    return (
 <div className={`main-loading ${style ? 'dark-mode' : ''}`}>
        <div className="loading-course">
        <BrianAnimated style={style} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`profile-page ${style ? "dark-mode" : ""}`}>
        <SidebarProfile />
        <main className="profile-main-content">
          <Container fluid>
            <div className="page-header">
              <div className="header-icon-wrapper">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div className="header-text">
                <h1>المحفظة وسجل الشحن</h1>
                <p>اشحن محفظتك بسهولة وتابع كل عمليات الشحن السابقة.</p>
              </div>
            </div>

            <Row>
              <Col lg={4} className="mb-4">
                <div className="wallet-balance-card">
                  <span className="wallet-card-title">رصيدك الحالي</span>
                  <div className="balance">
                    <h2>{walletBalance}</h2>
                    <span>جنيه</span>
                  </div>
                  <div className="wallet-icon-bg">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                </div>
              </Col>
              <Col lg={8} className="mb-4">
                <div className="charge-options-card">
                  <div className="charge-option">
                    <h5 className="card-header">
                      <FontAwesomeIcon icon={faBarcode} /> شحن باستخدام كود
                    </h5>
                    <form onSubmit={handleCharge}>
                      <input
                        type="text"
                        placeholder="أدخل كود الشحن هنا"
                        value={code}
                        onChange={(e) => {
                          const value = e.target.value;
                          
                          // تطبيق الحماية من XSS على كود الشحن
                          const securedValue = secureWalletInput(value, 'code');
                          
                          // إذا تم حذف المحتوى بسبب XSS، أظهر تحذير
                          if (value && !securedValue && value.trim() !== '') {
                            swal("تحذير أمني!", "كود الشحن يحتوي على أحرف غير مسموحة", "warning");
                            return;
                          }
                          
                          setCode(securedValue);
                        }}
                      />
                      <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <Spinner as="span" animation="border" size="sm" />
                        ) : (
                          "اشحن الآن"
                        )}
                      </button>
                    </form>
                  </div>
                  <div className="charge-option">
                    <h5 className="card-header">
                      <FontAwesomeIcon icon={faStore} /> شحن عن طريق فوري
                    </h5>
                    <form onSubmit={chargeBalanceWithfawry}>
                      <input
                        type="number"
                        placeholder="أدخل المبلغ"
                        value={fawryAmount}
                        onChange={(e) => {
                          const value = e.target.value;
                          
                          // تطبيق الحماية من XSS على مبلغ فوري
                          const securedValue = secureWalletInput(value, 'amount');
                          
                          // إذا تم حذف المحتوى بسبب XSS، أظهر تحذير
                          if (value && !securedValue && value.trim() !== '') {
                            swal("تحذير أمني!", "المبلغ يحتوي على أحرف غير مسموحة", "warning");
                            return;
                          }
                          
                          setFawryAmount(securedValue);
                        }}
                      />
                      <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <Spinner as="span" animation="border" size="sm" />
                        ) : (
                          "اطلب كود فوري"
                        )}
                      </button>
                    </form>
                  </div> 
                </div>
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                {codesTable.length > 0 ? (
                  <div className="table-container-wrapper">
                    <div className="datatable-wrapper">
                      {isPagingLoading && (
                        <div className="table-loading-overlay">
                          <Spinner
                            animation="border"
                            variant={style ? "light" : "danger"}
                          />
                        </div>
                      )}
                      <DataTable
                        columns={columns}
                        data={currentItems}
                        customStyles={customStyles}
                        noDataComponent={
                          <div style={{ padding: "24px" }}>
                            لا توجد سجلات لعرضها
                          </div>
                        }
                      />
                    </div>
                    <ReactPaginate
                      previousLabel={"السابق"}
                      nextLabel={"التالي"}
                      breakLabel={"..."}
                      pageCount={pageCount}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={3}
                      onPageChange={handlePageClick}
                      containerClassName={"pagination-container"}
                      pageLinkClassName={"pagination-link"}
                      previousLinkClassName={"pagination-link prev-next"}
                      nextLinkClassName={"pagination-link prev-next"}
                      breakLinkClassName={"pagination-link"}
                      activeClassName={"pagination-active"}
                      disabledClassName={"pagination-disabled"}
                    />
                  </div>
                ) : (
                  <div className="no-content-container">
                    <div className="icon-wrapper-not">
                      <span className="icon-line line-1"></span>
                      <span className="icon-line line-2"></span>
                    </div>
                    <h2> مفيش اي أكواد شحن ليك </h2>
                    {/* <p> يلا اشترك في الكورسات مستني ايه ! </p> */}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </main>
      </div>
      <ScrollTopButton />
    </>
  );
}

export default ProfileWallet;
